import React, { useState } from 'react';
import './EligibilityResults.css';

function classifyCoverageStatus(planStatuses) {
  const statuses = Array.isArray(planStatuses) ? planStatuses : [];
  const coverageStatuses = statuses.filter((ps) =>
    Array.isArray(ps.serviceTypeCodes) && ps.serviceTypeCodes.includes('30')
  );
  const relevantStatuses = coverageStatuses.length > 0 ? coverageStatuses : statuses;

  const hasActiveStatus = relevantStatuses.some(
    (ps) => ps.statusCode === '1' || ps.statusCode === '2'
  );
  const hasInactiveStatus = relevantStatuses.some(
    (ps) => ps.statusCode === '6' || ps.status?.toLowerCase() === 'inactive'
  );

  if (hasActiveStatus) return 'Active';
  if (hasInactiveStatus) return 'Inactive';
  return 'Unknown';
}

// Join a list of strings/elements with " · " separators, skipping falsy parts.
function dotJoin(parts) {
  return parts.filter(Boolean).map((p, i) => (
    <React.Fragment key={i}>{i > 0 ? ' · ' : ''}{p}</React.Fragment>
  ));
}

// ── 271 enrichment helpers — derive richer views from benefitsInformation ──
function fmtPhone(n) {
  if (!n) return null;
  const d = String(n).replace(/\D/g, '');
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : String(n);
}

function fmtEdiDate(s) {
  const start = String(s || '').split('-')[0];
  if (/^\d{8}$/.test(start)) return `${start.slice(4, 6)}/${start.slice(6, 8)}/${start.slice(0, 4)}`;
  return null;
}

function fmtAddress(a) {
  if (!a) return null;
  const zip = String(a.postalCode || '').replace(/^(\d{5})(\d{4})$/, '$1-$2');
  const cityState = [a.city, a.state].filter(Boolean).join(', ');
  return [a.address1, a.address2, [cityState, zip].filter(Boolean).join(' ').trim()]
    .filter(Boolean)
    .join(', ');
}

function entityContact(e, mode) {
  const contacts = (e && e.contactInformation && e.contactInformation.contacts) || [];
  const hit = contacts.find((c) => (c.communicationMode || '').toLowerCase() === mode);
  return hit ? hit.communicationNumber : null;
}

function firstRelatedEntity(b) {
  return (Array.isArray(b.benefitsRelatedEntities) && b.benefitsRelatedEntities[0])
    || b.benefitsRelatedEntity
    || null;
}

function entityDisplayName(e) {
  if (e.entityFirstname) return `${e.entityName}, ${e.entityFirstname}`; // Person: LAST, FIRST
  return e.entityName || null;
}

// Codes N (Services Restricted/IPA) and L (Primary Care Provider) carry the
// provider-restriction loops in benefitsRelatedEntities.
function deriveNetworkRestriction(benefits) {
  const entities = [];
  for (const b of benefits) {
    if (b.code !== 'N' && b.code !== 'L') continue;
    const e = firstRelatedEntity(b);
    if (!e) continue;
    entities.push({
      role: b.code === 'N' ? 'Care Coordinated Through' : 'Primary Care Provider',
      name: entityDisplayName(e),
      entityType: b.code === 'N' ? (e.entityIdentifier || null) : null,
      npi: e.entityIdentification === 'XX' ? e.entityIdentificationValue : null,
      phone: fmtPhone(entityContact(e, 'telephone')),
      fax: fmtPhone(entityContact(e, 'facsimile')),
      address: fmtAddress(e.address),
      since: fmtEdiDate(b.benefitsDateInformation && b.benefitsDateInformation.primaryCareProvider),
    });
  }
  // Restriction (N) first, then PCP (L)
  entities.sort(
    (a, b) =>
      (a.role === 'Care Coordinated Through' ? 0 : 1) - (b.role === 'Care Coordinated Through' ? 0 : 1)
  );
  return entities.length ? { entities } : null;
}

// Benefit entries flagged authOrCertIndicator === 'Y' need auth/cert; surface
// the distinct service types plus the Utilization Management contact.
function deriveAuthorization(benefits) {
  const services = [
    ...new Set(
      benefits
        .filter((b) => b.authOrCertIndicator === 'Y')
        .flatMap((b) => b.serviceTypes || [])
    ),
  ];
  if (!services.length) return null;
  let um = null;
  for (const b of benefits) {
    const e = firstRelatedEntity(b);
    if (e && /utilization management/i.test(e.entityIdentifier || '')) { um = e; break; }
  }
  return {
    items: [{
      service: services.length > 4 ? `${services.length} service types` : services.join(', '),
      note: 'auth/cert may apply',
      contactLabel: 'Utilization Management',
      phone: um ? fmtPhone(entityContact(um, 'telephone')) : null,
    }],
  };
}

// Human label for a service type code, read from the response's paired
// serviceTypeCodes / serviceTypes arrays.
function serviceTypeLabel(benefits, code) {
  for (const b of benefits) {
    const codes = b.serviceTypeCodes || [];
    const i = codes.indexOf(code);
    if (i !== -1) return (b.serviceTypes || [])[i] || null;
  }
  return null;
}

function fmtBenefitAmount(b) {
  return b.benefitAmount != null
    ? '$' + Number(b.benefitAmount).toLocaleString('en-US', { maximumFractionDigits: 2 })
    : null;
}

function fmtBenefitPercent(b) {
  if (b.benefitPercent == null) return null;
  const v = Number(b.benefitPercent);
  return (v <= 1 ? Math.round(v * 100) : Math.round(v)) + '%';
}

function vendorRoleLabel(id) {
  if (/vendor/i.test(id)) return 'Managed by';
  if (/utilization management/i.test(id)) return 'Prior auth';
  if (/other physician|health care/i.test(id)) return 'Provider';
  return id || 'Contact';
}

function titleCase(s) {
  return String(s || '').toLowerCase().replace(/\b([a-z])/g, (m, c) => c.toUpperCase());
}

// Pull the cost-share detail + delegated vendors for one service type.
function deriveServiceTypeDetail(benefits, code) {
  const entries = benefits.filter((b) => (b.serviceTypeCodes || []).includes(code));
  if (!entries.length) return { empty: true, covered: false, rows: [], vendors: [] };

  const covered = entries.some((b) => b.code === '1');

  // Delegated vendors / orgs attached to this type's entries — e.g. Home Health
  // managed by "CARELON POST ACUTE SOLUTIONS". Skip the payer itself and the
  // IPA/PCP loops (those render in Network Information).
  const vendors = [];
  const seen = new Set();
  for (const b of entries) {
    const ents =
      (Array.isArray(b.benefitsRelatedEntities) && b.benefitsRelatedEntities) ||
      (b.benefitsRelatedEntity ? [b.benefitsRelatedEntity] : []);
    for (const e of ents) {
      const id = e.entityIdentifier || '';
      if (/party performing verification|independent physicians|primary care provider/i.test(id)) {
        continue;
      }
      if (!/vendor|utilization management|other physician|health care/i.test(id)) continue;
      const name = e.entityName || (e.contactInformation && e.contactInformation.name);
      if (!name) continue;
      const key = `${name}|${id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      vendors.push({
        role: vendorRoleLabel(id),
        name,
        phone: fmtPhone(entityContact(e, 'telephone')),
        npi: e.entityIdentification === 'XX' ? e.entityIdentificationValue : null,
      });
    }
  }

  // Sub-services: some payers (e.g. Surest) itemize one service type into several
  // sub-benefits via additionalInformation — Home Health → Visit / Infusion /
  // Dialysis / Private Duty, each with its own coverage, co-pay, and limit.
  const descOf = (b) =>
    (b.additionalInformation || []).map((a) => a.description).filter(Boolean).join(' / ');
  const subMap = new Map();
  for (const b of entries) {
    const d = descOf(b);
    if (!d) continue;
    if (!subMap.has(d)) subMap.set(d, []);
    subMap.get(d).push(b);
  }
  const pickByCode = (es, c) =>
    es.find((b) => b.code === c && b.inPlanNetworkIndicatorCode === 'Y') ||
    es.find((b) => b.code === c);
  const subServices = [];
  for (const [name, es] of subMap) {
    const copayB = pickByCode(es, 'B');
    const coinsA = pickByCode(es, 'A');
    const dedC = pickByCode(es, 'C');
    const limitF = es.find((b) => b.code === 'F' && b.benefitQuantity);
    subServices.push({
      name: titleCase(name),
      covered: es.some((b) => b.code === '1'),
      nonCovered: es.some((b) => b.code === 'I'),
      copay: copayB && fmtBenefitAmount(copayB)
        ? fmtBenefitAmount(copayB) + (copayB.timeQualifier === 'Visit' ? ' / visit' : '')
        : null,
      coinsurance: coinsA ? fmtBenefitPercent(coinsA) : null,
      deductible: dedC ? fmtBenefitAmount(dedC) : null,
      limit: limitF
        ? `${limitF.benefitQuantity} ${(limitF.quantityQualifier || 'units').toLowerCase()}` +
          (limitF.timeQualifier ? ` (${limitF.timeQualifier.toLowerCase()})` : '')
        : null,
    });
  }

  const buildRow = (label, benefitCode, fmt) => {
    const es = entries.filter((b) => b.code === benefitCode && !descOf(b));
    if (!es.length) return null;
    const inN =
      es.find((b) => b.inPlanNetworkIndicatorCode === 'Y') ||
      es.find((b) => !b.inPlanNetworkIndicatorCode || b.inPlanNetworkIndicatorCode === 'W');
    const outN = es.find((b) => b.inPlanNetworkIndicatorCode === 'N');
    return { metric: label, inNet: inN ? fmt(inN) : null, outNet: outN ? fmt(outN) : null };
  };

  const rows = [
    buildRow('Co-pay', 'B', fmtBenefitAmount),
    buildRow('Co-insurance', 'A', fmtBenefitPercent),
    buildRow('Deductible', 'C', fmtBenefitAmount),
    buildRow('Out-of-pocket', 'G', fmtBenefitAmount),
  ].filter(Boolean);

  return { empty: false, covered, rows, vendors, subServices };
}

// Collapsible (caret on the right), collapsed by default.
function ServiceTypeBox({ code, label, detail }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="er-stbox">
      <button type="button" className="er-stbox-header" onClick={() => setOpen((o) => !o)}>
        <span className="er-stbox-title">
          <span className="er-stbox-code">{code}</span>
          <span>{label || 'Service Type'}</span>
        </span>
        <span className={`er-stbox-caret ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="er-stbox-body">
          {detail.empty ? (
            <p className="er-na">No benefits returned for this service type in this check.</p>
          ) : (
            <>
              {detail.subServices && detail.subServices.length > 0 ? (
                <div className="er-subsvc-list">
                  {detail.subServices.map((s, i) => (
                    <div className="er-subsvc" key={i}>
                      <div className="er-subsvc-head">
                        <span className="er-subsvc-name">{s.name}</span>
                        {s.nonCovered ? (
                          <span className="er-subsvc-tag nc">Not covered</span>
                        ) : s.covered ? (
                          <span className="er-subsvc-tag cov">Covered</span>
                        ) : null}
                      </div>
                      {(s.copay || s.coinsurance || s.deductible || s.limit) && (
                        <div className="er-subsvc-metrics">
                          {s.copay && <span><b>{s.copay}</b> co-pay</span>}
                          {s.coinsurance && <span><b>{s.coinsurance}</b> co-insurance</span>}
                          {s.deductible && <span><b>{s.deductible}</b> deductible</span>}
                          {s.limit && <span>Limit: <b>{s.limit}</b></span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : detail.rows.length > 0 ? (
                <table className="er-bentable">
                  <thead>
                    <tr><th>Metric</th><th>In-Network</th><th>Out-of-Network</th></tr>
                  </thead>
                  <tbody>
                    {detail.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="metric">{r.metric}</td>
                        <td className={r.inNet ? 'val' : 'er-na'}>{r.inNet || '—'}</td>
                        <td className={r.outNet ? 'val' : 'er-na'}>{r.outNet || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                detail.vendors.length === 0 && (
                  <p className="er-na">
                    {detail.covered
                      ? 'Active coverage; no cost-share detail returned.'
                      : 'No cost-share detail returned.'}
                  </p>
                )
              )}
              {detail.vendors.length > 0 && (
                <div className="er-stbox-vendors">
                  {detail.vendors.map((v, i) => (
                    <div className="er-stbox-vendor" key={i}>
                      <span className="er-stbox-vendor-role">{v.role}</span>
                      <span className="er-stbox-vendor-name">{v.name}</span>
                      {(v.npi || v.phone) && (
                        <span className="er-stbox-vendor-meta">
                          {dotJoin([v.npi && `NPI ${v.npi}`, v.phone && `☎ ${v.phone}`])}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EligibilityResults({ data, onShowHistory, formContext }) {
  const [view, setView] = useState('formatted'); // 'formatted' | 'raw'

  if (!data) return null;

  const ctx = formContext || {};
  const subscriber = data.subscriber || {};
  const payer = data.payer || {};
  const planInfo = data.planInformation || {};
  const planStatuses = Array.isArray(data.planStatus) ? data.planStatus : [];
  const benefits = data.benefitsInformation || [];
  const planDates = data.planDateInformation || {};
  const encounter = data.encounter || {};
  const providerInfo271 = data.provider || {};

  // Per-type boxes are driven by what the 271 actually returned (one request
  // can come back with several types). Order the requested type first.
  const requestedType = ctx.serviceType || null;
  const responseServiceTypes = [
    ...new Set(benefits.flatMap((b) => b.serviceTypeCodes || [])),
  ].sort((a, b) => {
    if (a === requestedType) return -1;
    if (b === requestedType) return 1;
    return a.localeCompare(b, undefined, { numeric: true });
  });

  // Enriched views derived from the raw 271 (the response already carries these
  // loops). Each render block below is gated, so a check lacking them renders
  // exactly as before.
  const networkRestriction = deriveNetworkRestriction(benefits);
  const authorization = deriveAuthorization(benefits);
  const coordinationOfBenefits = null;
  const benefitsByService = [];
  const activeCoverage = benefits.find((b) => b.code === '1') || {};
  const insuranceType = activeCoverage.insuranceType || null;
  // "Medicare Primary" (insuranceTypeCode MP) on a managed plan => Medicare Advantage
  const isMedicareAdvantage =
    activeCoverage.insuranceTypeCode === 'MP' || /medicare/i.test(insuranceType || '');
  const coverageTypeLabel = isMedicareAdvantage ? 'Medicare Advantage' : null;
  const planType = insuranceType;
  const caseNumber = planInfo.caseNumber || null;
  const isRestricted = (networkRestriction?.entities || []).some(
    (e) => /ipa|independent/i.test(e.entityType || '')
  );

  // Merge provider from 271 response with form context (LHS provider dropdown)
  const ctxProv = ctx.provider || {};
  const providerInfo = {
    firstName: providerInfo271.firstName || ctxProv.first_name || '',
    lastName: providerInfo271.lastName || ctxProv.last_name || '',
    npi: providerInfo271.npi || ctxProv.npi || '',
  };

  const coverageStatus = classifyCoverageStatus(planStatuses);
  const isActive = coverageStatus === 'Active';

  const formatDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return 'N/A';
    if (dateStr.includes('-') && dateStr.length > 10) {
      const [start] = dateStr.split('-');
      dateStr = start;
    }
    if (/^\d{8}$/.test(dateStr)) {
      const y = dateStr.slice(0, 4), m = dateStr.slice(4, 6), d = dateStr.slice(6, 8);
      return `${m}/${d}/${y}`;
    }
    return dateStr;
  };

  // Extract benefits
  const getBenefitValue = (code, serviceType) => {
    const match = benefits.find(b =>
      b.code === code &&
      (b.inPlanNetworkIndicatorCode === 'Y' || !b.inPlanNetworkIndicatorCode) &&
      (b.coverageLevelCode === 'IND' || !b.coverageLevelCode) &&
      (!serviceType || b.serviceTypeCodes?.includes(serviceType))
    );
    return match;
  };

  const deductibleBenefit = getBenefitValue('C', '30') || getBenefitValue('C');
  const deductibleRemaining = benefits.find(b =>
    b.code === 'C' && b.timeQualifierCode === '29' &&
    (b.inPlanNetworkIndicatorCode === 'Y' || !b.inPlanNetworkIndicatorCode) &&
    (b.coverageLevelCode === 'IND' || !b.coverageLevelCode)
  );
  const oopBenefit = getBenefitValue('G', '30') || getBenefitValue('G');
  const oopRemaining = benefits.find(b =>
    b.code === 'G' && b.timeQualifierCode === '29' &&
    (b.inPlanNetworkIndicatorCode === 'Y' || !b.inPlanNetworkIndicatorCode) &&
    (b.coverageLevelCode === 'IND' || !b.coverageLevelCode)
  );

  // Service type codes live in planStatus entries, not encounter
  const serviceTypeCodes = [...new Set(planStatuses.flatMap(ps => ps.serviceTypeCodes || []))];
  // DOS: prefer encounter, fall back to form context
  const dosStr = encounter?.dateOfService
    ? formatDate(encounter.dateOfService)
    : ctx.dateOfService || 'N/A';

  // Plan name: prefer planStatus[].planDetails, then planInformation.planDescription
  // Prefer the fuller plan-name field: planStatus.planDetails is often truncated
  // to 50 chars, while planInformation.planDescription may carry the complete
  // name (and plan type). Pick the longer of the two non-empty values.
  const planName = [
    planStatuses.find(ps => ps.planDetails)?.planDetails,
    planInfo.planDescription,
  ].filter(Boolean).sort((a, b) => b.length - a.length)[0] || 'N/A';

  const ts = data._checkedAt ? new Date(data._checkedAt) : new Date();
  const timeStr = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ', ' + ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="eligibility-results">
      {/* Header */}
      <div className="er-header-block">
        <div className="er-header">
          <div className="er-header-left">
            <h2>Eligibility Check</h2>
            <span className="er-timestamp">{timeStr}</span>
            <span className={`er-status-badge ${isActive ? 'active' : 'inactive'}`}>
              {isActive ? 'Active Coverage' : coverageStatus}
            </span>
            {isRestricted && <span className="er-chip restricted">IPA</span>}
            {coverageTypeLabel && <span className="er-chip dual">{coverageTypeLabel}</span>}
          </div>
          <div className="er-header-right">
            {onShowHistory && (
              <button
                className={`er-toggle-btn ${view === 'history' ? 'active' : ''}`}
                onClick={() => { setView('formatted'); onShowHistory(); }}
              >
                History
              </button>
            )}
            <button
              className={`er-toggle-btn ${view === 'raw' ? 'active' : ''}`}
              onClick={() => setView(view === 'raw' ? 'formatted' : 'raw')}
            >
              Raw JSON
            </button>
          </div>
        </div>
        {planName !== 'N/A' && (
          <div className="er-plan-subtitle">{planName}</div>
        )}
      </div>

      {view === 'raw' ? (
        <pre className="er-raw-json">{JSON.stringify(data, null, 2)}</pre>
      ) : (
        <div className="er-sections">
          {/* Subscriber */}
          <div className="er-section">
            <h3 className="er-section-title">Subscriber</h3>
            <div className="er-grid er-grid-5">
              <span className="er-label">First name</span>
              <span className="er-label">Last name</span>
              <span className="er-label">Date of birth</span>
              <span className="er-label">Gender</span>
              <span className="er-label">Member ID</span>
              <span className="er-value">{subscriber.firstName || 'N/A'}</span>
              <span className="er-value">{subscriber.lastName || 'N/A'}</span>
              <span className="er-value">{formatDate(subscriber.dateOfBirth)}</span>
              <span className="er-value">{subscriber.gender === 'M' ? 'Male' : subscriber.gender === 'F' ? 'Female' : 'N/A'}</span>
              <span className="er-value">{subscriber.memberId || 'N/A'}</span>
            </div>
            {subscriber.address && (
              <div className="er-grid er-grid-5 er-address-grid">
                <span className="er-label er-span-2">Address</span>
                <span className="er-label">City</span>
                <span className="er-label">State</span>
                <span className="er-label">Postal code</span>
                <span className="er-value er-span-2">
                  {[subscriber.address.address1, subscriber.address.address2].filter(Boolean).join(', ') || '-'}
                </span>
                <span className="er-value">{subscriber.address.city || '-'}</span>
                <span className="er-value">{subscriber.address.state || '-'}</span>
                <span className="er-value">{subscriber.address.postalCode || '-'}</span>
              </div>
            )}
          </div>

          {/* Encounter (+ payer, on one line; Date of Service lives on the left form) */}
          <div className="er-section">
            <h3 className="er-section-title">Encounter</h3>
            <div className="er-grid er-grid-5">
              <span className="er-label">Organization</span>
              <span className="er-label">Provider</span>
              <span className="er-label">NPI</span>
              <span className="er-label">Payer</span>
              <span className="er-label">Payer ID</span>
              <span className="er-value">{providerInfo271.providerName || 'N/A'}</span>
              <span className="er-value">{`${providerInfo.firstName || ''} ${providerInfo.lastName || ''}`.trim() || 'N/A'}</span>
              <span className="er-value">{providerInfo.npi || 'N/A'}</span>
              <span className="er-value">{payer.name || ctx.payerName || 'N/A'}</span>
              <span className="er-value">{ctx.payerId || payer.entityIdentifier || 'N/A'}</span>
            </div>
          </div>

          {/* Plan */}
          <div className="er-section">
            <h3 className="er-section-title">Plan</h3>
            {planType ? (
              <>
                <div className="er-grid er-grid-4">
                  <span className="er-label">Plan</span>
                  <span className="er-label">Type</span>
                  <span className="er-label">Group #</span>
                  <span className="er-label">Effective</span>
                  <span className="er-value">{planName}</span>
                  <span className="er-value">{planType}</span>
                  <span className="er-value">{planInfo.groupNumber || 'N/A'}</span>
                  <span className="er-value">{formatDate(planDates.planBegin)}</span>
                </div>
                <div className="er-sub-label">More</div>
                <div className="er-grid er-grid-4">
                  <span className="er-label">Group</span>
                  <span className="er-label">Case #</span>
                  <span className="er-label"></span>
                  <span className="er-label"></span>
                  <span className="er-value">{planInfo.groupDescription || planInfo.groupNumber || 'N/A'}</span>
                  <span className="er-value">{caseNumber || '-'}</span>
                  <span className="er-value"></span>
                  <span className="er-value"></span>
                </div>
              </>
            ) : (
              <div className="er-grid er-grid-4">
                <span className="er-label">Plan</span>
                <span className="er-label">Group</span>
                <span className="er-label">Group #</span>
                <span className="er-label">Effective</span>
                <span className="er-value">{planName}</span>
                <span className="er-value">{planInfo.groupDescription || planInfo.groupNumber || 'N/A'}</span>
                <span className="er-value">{planInfo.groupNumber || 'N/A'}</span>
                <span className="er-value">{formatDate(planDates.planBegin)}</span>
              </div>
            )}
          </div>

          {/* Network Restriction (PCP / IPA "Services Restricted" loops) */}
          {networkRestriction?.entities?.length > 0 && (
            <div className="er-section er-flag">
              <h3 className="er-section-title">
                Network Information
                <span className="er-flag-hint">
                  &mdash; care is coordinated through the IPA (referrals &amp; prior auth route here)
                </span>
              </h3>
              {networkRestriction.entities.map((e, i) => (
                <div className="er-entity" key={i}>
                  <span className="er-entity-role">{e.role}</span>
                  <span className="er-entity-name">{e.name}</span>
                  {(e.entityType || e.npi || e.phone) && (
                    <span className="er-entity-meta">
                      {dotJoin([
                        e.entityType,
                        e.npi && (<>NPI <span className="npi" key="npi">{e.npi}</span></>),
                        e.phone && `☎ ${e.phone}`,
                        e.address,
                      ])}
                    </span>
                  )}
                  {(e.fax || e.since) && (
                    <span className="er-entity-meta">
                      {dotJoin([
                        e.fax && `Fax ${e.fax}`,
                        e.since && `${e.role === 'Primary Care Provider' ? 'PCP since' : 'Since'} ${e.since}`,
                      ])}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Authorization */}
          {authorization?.items?.length > 0 && (
            <div className="er-section">
              <h3 className="er-section-title">Authorization</h3>
              {authorization.items.map((a, i) => (
                <div className="er-auth" key={i}>
                  <span className="er-badge-auth">
                    {a.service}{a.note ? ` — ${a.note}` : ''}
                  </span>
                  {a.phone && (
                    <span className="er-contact">
                      {a.contactLabel || 'Contact'} &mdash; <strong>&#9742; {a.phone}</strong>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Coordination of Benefits */}
          {coordinationOfBenefits && (
            <div className="er-section">
              <h3 className="er-section-title">Coordination of Benefits</h3>
              <div className="er-grid er-grid-2">
                <span className="er-label">This payer</span>
                <span className="er-label">Other payers</span>
                <span className="er-value">{coordinationOfBenefits.thisPayer || '-'}</span>
                <span className={`er-value ${coordinationOfBenefits.otherPayers?.length ? '' : 'er-na'}`}>
                  {coordinationOfBenefits.otherPayers?.length
                    ? coordinationOfBenefits.otherPayers.join(', ')
                    : 'None reported'}
                </span>
              </div>
            </div>
          )}

          {/* Benefits — enriched per-service view when available, else legacy bars */}
          {benefitsByService.length > 0 ? (
            benefitsByService.map((s, i) => (
              <div className="er-section" key={i}>
                <h3 className="er-section-title">Benefits &mdash; {s.title}</h3>
                {s.rows?.length > 0 && (
                  <table className="er-bentable">
                    <thead>
                      <tr><th>Metric</th><th>In-Network</th><th>Out-of-Network</th></tr>
                    </thead>
                    <tbody>
                      {s.rows.map((r, j) => (
                        <tr key={j}>
                          <td className="metric">{r.metric}</td>
                          <td className={r.inNet ? 'val' : 'er-na'}>{r.inNet || '—'}</td>
                          <td className={r.outNet ? 'val' : 'er-na'}>{r.outNet || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {s.oop && (
                  <BenefitBar
                    label="Out-of-Pocket Max"
                    total={s.oop.total || 0}
                    remaining={s.oop.remaining != null ? s.oop.remaining : null}
                    barClass="bar-oop"
                  />
                )}
                {s.coveredServices?.length > 0 && (
                  <details className="er-inclusions">
                    <summary>Covered services ({s.coveredServices.length})</summary>
                    <div className="er-pills">
                      {s.coveredServices.map((c, k) => (
                        <span className="er-pill" key={k}>{c}</span>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))
          ) : (
            (deductibleBenefit || oopBenefit) && (
              <div className="er-section">
                <h3 className="er-section-title">Benefits</h3>
                {deductibleBenefit && (
                  <BenefitBar
                    label="Deductible"
                    total={parseFloat(deductibleBenefit.benefitAmount) || 0}
                    remaining={deductibleRemaining ? parseFloat(deductibleRemaining.benefitAmount) : null}
                    barClass="bar-deductible"
                  />
                )}
                {oopBenefit && (
                  <BenefitBar
                    label="Out-of-Pocket Max"
                    total={parseFloat(oopBenefit.benefitAmount) || 0}
                    remaining={oopRemaining ? parseFloat(oopRemaining.benefitAmount) : null}
                    barClass="bar-oop"
                  />
                )}
              </div>
            )
          )}

          {/* Coverage by Service Type — one collapsible box per returned type */}
          {responseServiceTypes.length > 0 && (
            <div className="er-section">
              <h3 className="er-section-title">Coverage by Service Type</h3>
              <div className="er-stbox-list">
                {responseServiceTypes.map((code) => (
                  <ServiceTypeBox
                    key={code}
                    code={code}
                    label={serviceTypeLabel(benefits, code)}
                    detail={deriveServiceTypeDetail(benefits, code)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BenefitBar({ label, total, remaining, barClass }) {
  const pct = total > 0 && remaining !== null ? Math.min((remaining / total) * 100, 100) : 0;
  const fmt = (v) => '$' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="er-benefit-row">
      <div className="er-benefit-header">
        <span className="er-benefit-label">{label}</span>
        {remaining !== null ? (
          <span className="er-benefit-amount">{fmt(remaining)} remaining of {fmt(total)}</span>
        ) : (
          <span className="er-benefit-amount">{fmt(total)}</span>
        )}
      </div>
      <div className="er-benefit-track">
        <div className={`er-benefit-fill ${barClass || ''}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default EligibilityResults;
